import { useEffect, type RefObject } from "react";
import { useKeyedState } from "@/hooks/useKeyedState";

/** mt-4 + featured card (illustration, copy, button, padding). */
const FEATURED_BOX_VERTICAL_RESERVE_PX = 176;
const FIT_BUFFER_PX = 8;

/**
 * Hides the sidebar Slack featured box when there isn't enough room between the
 * nav block and the footer for the card.
 *
 * `useEffect` (not `useLayoutEffect`) so the footer ref, which is attached to a
 * later sibling of the placement's subtree, is populated by the time we
 * measure on the initial mount.
 */
export function useSidebarFeaturedBoxVerticalFit(
  navBlockRef: RefObject<HTMLElement | null>,
  footerAnchorRef: RefObject<HTMLElement | null>,
  enabled: boolean,
) {
  const [hasVerticalSpace, setHasVerticalSpace] = useKeyedState(enabled, false);

  useEffect(() => {
    if (!enabled) return;

    const navEl = navBlockRef.current;
    const footerEl = footerAnchorRef.current;
    if (!navEl || !footerEl) {
      return;
    }

    let rafId: number | null = null;

    const measure = () => {
      rafId = null;
      const available =
        footerEl.getBoundingClientRect().top -
        navEl.getBoundingClientRect().bottom;
      const needed = FEATURED_BOX_VERTICAL_RESERVE_PX + FIT_BUFFER_PX;
      setHasVerticalSpace((prev) => {
        const next = available >= needed;
        return prev === next ? prev : next;
      });
    };

    const scheduleMeasure = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(measure);
    };

    measure();

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(navEl);
    observer.observe(footerEl);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [enabled, footerAnchorRef, navBlockRef, setHasVerticalSpace]);

  return hasVerticalSpace;
}
